import _ from 'lodash';
import { calculateReviewedStatus } from '@/widgets/beats/ui/reviewBeatsHelper';
import { useReportStore } from '@/entities/report';

/**
 * Backs the RR heat map footer toggle that hides cells whose beats are all reviewed. Subscribes to isHideReviewedCell and the selected cell list, and on a genuine click flips the toggle and drops any selected cell that comes back fully reviewed once the pending bulk, reset and per-beat review edits are applied.
 *
 * @returns An object carrying isHideReviewedCell and the onChangeHideReviewed handler.
 */
const useFooterChart = () => {
  const isHideReviewedCell = useReportStore((s) => s.isHideReviewedCell);
  const setIsHideReviewedCell = useReportStore((s) => s.actions.setIsHideReviewedCell);
  const listSelectedCells = useReportStore((s) => s.listSelectedCells);
  const setListSelectedCells = useReportStore((s) => s.actions.setListSelectedCells);
  /**
   * Reads the pending reset entries for bulk beat review straight off the report store. Bypasses the hook subscription by calling getState so callbacks see the current value without re-rendering.
   *
   * @returns The current resetBulkReviewedBeats list.
   */
  const getResetBulkReviewedBeats = () => useReportStore.getState().resetBulkReviewedBeats;
  /**
   * Reads the pending bulk beat-review entries straight off the report store. Bypasses the hook subscription by calling getState so callbacks see the current value without re-rendering.
   *
   * @returns The current bulkReviewedBeats list.
   */
  const getBulkReviewedBeats = () => useReportStore.getState().bulkReviewedBeats;
  /**
   * Reads the pending per-beat review changes straight off the report store. Bypasses the hook subscription by calling getState so callbacks see the current value without re-rendering.
   *
   * @returns The current reviewedBeatsChanges list.
   */
  const getReviewedBeatsChanges = () => useReportStore.getState().reviewedBeatsChanges;

  /**
   * Flips the hide-reviewed toggle and prunes the selection down to cells that are still unreviewed. Ignores non-click events, inverts isHideReviewedCell, then recomputes each selected cell's reviewed status from the live bulk, reset and per-beat review edits, writing the shorter list back only when something actually dropped.
   *
   * @param checked - New toggle value supplied by the switch; unused, since the flip is derived from the previous state.
   * @param event - Originating DOM event, acted on only when its type is a click.
   * @returns Nothing.
   */
  const onChangeHideReviewed = (checked: boolean, event: any) => {
    if (event?.type !== 'click') {
      return;
    }

    setIsHideReviewedCell((prev) => !prev);
    const newListSelectedCells: any[] = [];
    _.forEach(listSelectedCells, (item) => {
      const filterBeatChanges = _.filter(getReviewedBeatsChanges(), (x) => x.cellId === item.id);
      const { isReviewed } = calculateReviewedStatus({
        item,
        resetBulkReviewedBeats: getResetBulkReviewedBeats(),
        filterBeatChanges,
        bulkReviewedBeats: getBulkReviewedBeats(),
      });
      if (!isReviewed) {
        newListSelectedCells.push(item);
      }
    });
    if (newListSelectedCells.length !== listSelectedCells.length) {
      setListSelectedCells(newListSelectedCells);
    }
  };

  return {
    isHideReviewedCell,
    onChangeHideReviewed,
  };
};

export default useFooterChart;
